"use client";

import { useEffect, useState } from "react";

// `navigator.onLine` only reflects the network interface (wifi/cellular attached),
// not real reachability: with wifi on but no uplink, or a cellular signal without
// data, it stays `true` and no `offline` event fires. So besides the browser
// events we probe a tiny public endpoint (no-store, see /api/build-version).
const PROBE_URL = "/api/build-version";
const PROBE_INTERVAL_MS = 15 * 1000;
const PROBE_TIMEOUT_MS = 5 * 1000;
// Consecutive failed probes before declaring offline, to avoid flashing the
// dialog on a single dropped request.
const FAILURES_BEFORE_OFFLINE = 2;

export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState(
    () => typeof navigator === "undefined" || navigator.onLine,
  );

  useEffect(() => {
    let cancelled = false;
    let failures = 0;

    const probe = async () => {
      if (document.visibilityState !== "visible") return;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
      try {
        // Any HTTP response (even 5xx) proves the network path works.
        await fetch(PROBE_URL, { cache: "no-store", signal: controller.signal });
        failures = 0;
        if (!cancelled) setIsOnline(true);
      } catch {
        failures += 1;
        if (!cancelled && failures >= FAILURES_BEFORE_OFFLINE) {
          setIsOnline(false);
        }
      } finally {
        clearTimeout(timeout);
      }
    };

    const handleOnline = () => {
      failures = 0;
      setIsOnline(true);
      void probe();
    };
    const handleOffline = () => setIsOnline(false);
    const handleVisibility = () => void probe();

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", handleVisibility);
    const interval = setInterval(() => void probe(), PROBE_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  return isOnline;
}
